import React from 'react';

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  /**
   * Grow with content from the `rows` baseline up to `maxHeight`, then scroll.
   * Off by default so existing fixed-height textareas are unaffected.
   */
  autoGrow?: boolean;
  /** Cap (px) for autoGrow before the textarea scrolls internally. */
  maxHeight?: number;
}

const TextArea: React.FC<TextAreaProps> = ({
  label,
  error,
  className = '',
  rows = 3,
  autoGrow = false,
  maxHeight = 220,
  value,
  ...props
}) => {
  const errorClass = error ? 'ring-2 ring-red-500' : '';
  const ref = React.useRef<HTMLTextAreaElement>(null);

  // Auto-grow: measure content height, clamp to maxHeight, and only show the
  // scrollbar once capped. The `rows`-derived CSS min-height acts as the floor
  // so an empty box still shows a few lines.
  React.useLayoutEffect(() => {
    if (!autoGrow) return;
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [autoGrow, maxHeight, value]);

  return (
    <div className="w-full">
      {label && (
        <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1 font-mono">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        className={`textarea ${errorClass} ${className}`}
        rows={rows}
        value={value}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  );
};

export default TextArea;
